import React, { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useJourney, PAYMENT_TERMS } from '../JourneyContext';
import SmartAddressInput from '../components/SmartAddressInput';
import { Card, Input, Select } from '../../ui';

// Collapsible section component
const Section = ({ title, description, icon, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="text-blue-500 dark:text-blue-400">{icon}</div>
          <div className="text-left">
            <h3 className="font-semibold text-gray-900 dark:text-white">{title}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
          </div>
        </div>
        <motion.svg
          animate={{ rotate: isOpen ? 180 : 0 }}
          className="w-5 h-5 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </motion.svg>
      </button>
      <motion.div
        initial={false}
        animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
      >
        <div className="p-4 space-y-4">{children}</div>
      </motion.div>
    </div>
  );
};

export default function CompanyDetailsStep({ onValidate }) {
  const { company, updateCompany } = useJourney();
  const [errors, setErrors] = useState({});

  const validate = useCallback(() => {
    const newErrors = {};
    const messages = [];

    if (!company.vatNumber || !company.vatNumber.trim()) {
      newErrors.vatNumber = 'VAT Number is required';
      messages.push('VAT Number');
    }
    if (!company.address || !company.address.trim()) {
      newErrors.address = 'Address is required';
      messages.push('Address');
    }
    if (!company.contactPerson || !company.contactPerson.trim()) {
      newErrors.contactPerson = 'Contact Person is required';
      messages.push('Contact Person');
    }
    if (!company.contactNumber || !company.contactNumber.trim()) {
      newErrors.contactNumber = 'Contact Number is required';
      messages.push('Contact Number');
    }
    const email = company.emailAddress || '';
    if (!email.trim()) {
      newErrors.emailAddress = 'Email Address is required';
      messages.push('Email Address');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.emailAddress = 'Enter a valid email address';
      messages.push('Valid Email Address');
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return {
        valid: false,
        message: `Please complete required company details: ${messages.join(', ')}`,
      };
    }
    return { valid: true };
  }, [company.address, company.contactNumber, company.contactPerson, company.emailAddress, company.vatNumber]);

  useEffect(() => {
    if (onValidate) {
      onValidate(validate);
    }
  }, [onValidate, validate]);

  const handleChange = (field, value) => {
    updateCompany({ [field]: value });
    // Clear error when field is modified
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Handle address selection from SmartAddressInput
  const handleAddressSelect = (addressData) => {
    updateCompany({
      address: addressData.address || company.address,
      city: addressData.city || company.city,
      province: addressData.province || company.province,
      postalCode: addressData.postalCode || company.postalCode,
      country: addressData.country || company.country,
    });
  };

  const paymentTermsOptions = PAYMENT_TERMS.map(term => ({
    value: term,
    label: term,
  }));

  return (
    <div className="space-y-4">
      <Card className="p-6 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
            Company Details
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Enter your company information. Required fields are marked with *.
          </p>
        </div>

        <div className="space-y-4">
          {/* Basic Information */}
          <Section
            title="Basic Information"
            description="Company name and registration details"
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            }
          >
            <Input
              label="Company Name"
              required
              value={company.name}
              onChange={(e) => handleChange('name', e.target.value)}
              error={errors.name}
              placeholder="Acme Corporation"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Registration Number"
                required
                value={company.registrationNumber}
                onChange={(e) => handleChange('registrationNumber', e.target.value)}
                error={errors.registrationNumber}
                placeholder="2024/123456/07"
              />
              <Input
                label="VAT Number"
                value={company.vatNumber}
                onChange={(e) => handleChange('vatNumber', e.target.value)}
                placeholder="4123456789"
              />
            </div>
          </Section>

          {/* Address */}
          <Section
            title="Address"
            description="Company physical address"
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            }
          >
            <SmartAddressInput
              value={company.address}
              onChange={(value) => handleChange('address', value)}
              onSelect={handleAddressSelect}
              placeholder="Start typing an address..."
              error={errors.address}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="City"
                value={company.city}
                onChange={(e) => handleChange('city', e.target.value)}
              />
              <Input
                label="Province / State"
                value={company.province}
                onChange={(e) => handleChange('province', e.target.value)}
              />
              <Input
                label="Postal Code"
                value={company.postalCode}
                onChange={(e) => handleChange('postalCode', e.target.value)}
              />
              <Input
                label="Country"
                value={company.country}
                onChange={(e) => handleChange('country', e.target.value)}
              />
            </div>
          </Section>

          {/* Contact Information */}
          <Section
            title="Contact Information"
            description="Primary and secondary contacts"
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            }
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Contact Person"
                value={company.contactPerson}
                onChange={(e) => handleChange('contactPerson', e.target.value)}
                placeholder="John Smith"
                error={errors.contactPerson}
              />
              <Input
                label="Contact Number"
                value={company.contactNumber}
                onChange={(e) => handleChange('contactNumber', e.target.value)}
                placeholder="+27 11 123 4567"
                error={errors.contactNumber}
              />
            </div>
            <Input
              label="Email Address"
              type="email"
              value={company.emailAddress}
              onChange={(e) => handleChange('emailAddress', e.target.value)}
              placeholder="contact@company.co.za"
              error={errors.emailAddress}
            />
            <Input
              label="Secondary Contact"
              value={company.secondaryContact}
              onChange={(e) => handleChange('secondaryContact', e.target.value)}
              placeholder="Optional backup contact"
            />
          </Section>

          {/* Billing Information */}
          <Section
            title="Billing Information"
            description="Invoicing and payment details"
            defaultOpen={false}
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
            }
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Billing Contact Name"
                value={company.billingContactName}
                onChange={(e) => handleChange('billingContactName', e.target.value)}
                placeholder="Finance Department"
              />
              <Input
                label="Billing Email"
                type="email"
                value={company.billingEmail}
                onChange={(e) => handleChange('billingEmail', e.target.value)}
                placeholder="accounts@company.co.za"
              />
            </div>
            <Input
              label="Billing Address"
              value={company.billingAddress}
              onChange={(e) => handleChange('billingAddress', e.target.value)}
              placeholder="Leave blank if same as company address"
            />
            <Select
              label="Payment Terms"
              options={paymentTermsOptions}
              value={company.paymentTerms}
              onChange={(e) => handleChange('paymentTerms', e.target.value)}
            />
          </Section>
        </div>
      </Card>
    </div>
  );
}

// register validator when mounted

